import type { ReactNode } from 'react'
import type { ModoLayout } from './useModoLayout'
import type { Tema } from './BarraAplicacao'

/**
 * Marca o contêiner da cena 3D no DOM.
 *
 * O ribbon é irmão do canvas, não seu ancestral, então todo toque na cena caía
 * no ramo "clicou fora" e fechava o painel aberto — inclusive o gesto de
 * pressionar um marcador 3D, que é justamente quando o painel precisa estar à
 * vista mostrando progresso e resultado. Um atributo explícito, e não a classe
 * de estilo, para que renomear CSS não apague o comportamento em silêncio.
 */
export const ATRIBUTO_VIEWPORT_3D = 'data-viewport-3d'

interface Props {
  tema: Tema
  modoLayout: ModoLayout
  /** Barra superior persistente. Igual nos seis módulos. */
  barraAplicacao: ReactNode
  /** Faixa de ferramentas do módulo atual. */
  ribbon: ReactNode
  /** Trilha vertical de ferramentas universais do 3D. Ausente no mobile. */
  trilha?: ReactNode
  /** Dock flutuante de câmera no viewport 3D. */
  dockCamera?: ReactNode
  /** O canvas 3D. É o protagonista: recebe toda a sobra de espaço. */
  canvas: ReactNode
  /** Painel de propriedades do que está selecionado. Só quando há seleção. */
  inspetor?: ReactNode
  /** Faixa fina de estado: módulo, seleção, ferramenta, simulação. */
  barraEstado?: ReactNode
  /** Dock inferior de grupos. Só no mobile. */
  dock?: ReactNode
  /** Central de registros, quando o módulo tem. */
  logs?: ReactNode
  rodape?: ReactNode
}

/**
 * Estrutura única do ambiente de trabalho.
 *
 * Os seis módulos compartilham esta casca. O que muda entre eles é o conteúdo
 * dos encaixes, nunca o esqueleto — é isso que faz o usuário não reaprender a
 * interface ao trocar de assunto.
 *
 * Os encaixes opcionais somem da árvore quando não há o que pôr neles, em vez de
 * ficarem presentes e escondidos por CSS. Uma coluna de grade sem elemento não
 * ocupa largura, então o canvas recupera o espaço sozinho quando o inspetor
 * fecha — que é a regra de "o canvas é o protagonista" expressa em layout, e não
 * em media query.
 */
export function Workspace({
  tema,
  modoLayout,
  barraAplicacao,
  ribbon,
  trilha,
  dockCamera,
  canvas,
  inspetor,
  barraEstado,
  dock,
  logs,
  rodape,
}: Props) {
  return (
    <div className={`app-shell tema-${tema} layout-${modoLayout}`} data-modo-layout={modoLayout}>
      {barraAplicacao}

      <main className="workspace">
        <div className="faixa-ferramentas-cenario">{ribbon}</div>

        <section className="laboratorio laboratorio-ribbon">
          {trilha}
          <div className="viewport-3d" {...{ [ATRIBUTO_VIEWPORT_3D]: '' }}>
            {canvas}
            {dockCamera}
          </div>
          {inspetor}
        </section>

        {barraEstado}
        {logs}
        {dock}
        {rodape}
      </main>
    </div>
  )
}
